/**
 * Grand Quack Auto — day/night cycle system.
 *
 * 5-minute real-time loop: dawn → noon → sunset → dusk. Mutates sun position,
 * sky/fog color, ambient/hemisphere intensities. Sunset frames are the
 * gorgeous shareable ones.
 */

import * as THREE from 'three';
import { DAY_NIGHT, LIGHTING } from '../config';
import type { System, World } from '../types';

let cycleTime = DAY_NIGHT.startOffset;

/** Normalized cycle time [0,1] — exported for neon system. */
export function getCycleT(): number { return cycleTime / DAY_NIGHT.cycleDuration; }

// Scratch colors to avoid per-frame allocation.
const _skyColor = new THREE.Color();
const _sunColor = new THREE.Color();
const _a = new THREE.Color();
const _b = new THREE.Color();

/** Sample a color from an array of [t, hex] keyframes via linear interpolation. */
function sampleColor(keys: readonly (readonly [number, number])[], t: number): THREE.Color {
  // Clamp t to [0, 1].
  const tt = t - Math.floor(t);
  let i = 0;
  for (; i < keys.length - 1; i++) {
    if (tt < keys[i + 1]![0]) break;
  }
  const [t0, c0] = keys[i]!;
  const [t1, c1] = keys[(i + 1) % keys.length]!;
  const range = t1 - t0 || 1;
  const f = Math.max(0, Math.min(1, (tt - t0) / range));
  _a.setHex(c0);
  _b.setHex(c1);
  _a.lerp(_b, f);
  return _a;
}

/** Sample a scalar value from [t, value] keyframes. */
function sampleScalar(keys: readonly (readonly [number, number])[], t: number): number {
  const tt = t - Math.floor(t);
  let i = 0;
  for (; i < keys.length - 1; i++) {
    if (tt < keys[i + 1]![0]) break;
  }
  const [t0, v0] = keys[i]!;
  const [t1, v1] = keys[(i + 1) % keys.length]!;
  const range = t1 - t0 || 1;
  const f = Math.max(0, Math.min(1, (tt - t0) / range));
  return v0 + (v1 - v0) * f;
}

// Cache scene light references (resolved on first render).
let sun: THREE.DirectionalLight | null = null;
let ambient: THREE.AmbientLight | null = null;
let hemi: THREE.HemisphereLight | null = null;

function findLights(world: World): void {
  world.scene.traverse((obj) => {
    if (obj instanceof THREE.DirectionalLight && !sun) sun = obj;
    else if (obj instanceof THREE.AmbientLight && !ambient) ambient = obj;
    else if (obj instanceof THREE.HemisphereLight && !hemi) hemi = obj;
  });
}

export const dayNightSystem: System = {
  name: 'dayNight',

  fixedUpdate(dt: number, _world: World) {
    cycleTime = (cycleTime + dt) % DAY_NIGHT.cycleDuration;
  },

  render(_dt: number, _alpha: number, world: World) {
    if (!sun) findLights(world);
    if (!sun || !ambient || !hemi) return;

    const t = cycleTime / DAY_NIGHT.cycleDuration; // 0 → 1

    // Sun orbit: circular path in XZ, elevation dips at sunset/dusk.
    const angle = t * Math.PI * 2 - Math.PI / 2; // start at east
    const elevation = DAY_NIGHT.sunElevation;
    const radius = DAY_NIGHT.sunOrbitRadius;
    sun.position.set(
      Math.cos(angle) * radius,
      elevation + Math.sin(angle) * elevation * 0.6,
      Math.sin(angle) * radius,
    );

    // Sun color.
    sampleColor(DAY_NIGHT.sunColors, t);
    _sunColor.copy(_a);
    sun.color.copy(_sunColor);

    // Sun intensity dims at dusk.
    sun.intensity = sampleScalar(DAY_NIGHT.sunIntensity, t);

    // Sky / fog color.
    sampleColor(DAY_NIGHT.skyColors, t);
    _skyColor.copy(_a);
    if (world.scene.background instanceof THREE.Color) {
      world.scene.background.copy(_skyColor);
    }
    if (world.scene.fog && 'color' in world.scene.fog) {
      (world.scene.fog as THREE.FogExp2).color.copy(_skyColor);
    }

    // Ambient intensity.
    ambient.intensity = sampleScalar(DAY_NIGHT.ambientIntensity, t);

    // Hemisphere intensity tracks ambient.
    hemi.intensity = sampleScalar(DAY_NIGHT.hemiIntensity, t);
  },
};
